import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useMutation } from "@apollo/react-hooks";
import FontAwesomeIcon from "@streeteasy/design-system/deprecated/Icons/FontAwesomeIcon";
import Toast from "@streeteasy/design-system/legacy/Toast";
import { gql } from "apollo-boost";
import { arrayOf, number, string } from "prop-types";

import { TOUR_LIKES_OPTIONS } from "../../constants/tourLikesOptions";
import { respondToTourRatingAdded } from "../../events/tourRatingAdded";
import TourLikesModal from "../TourLikesModal";
import * as S from "./styled";

const showLikes = rating => !(typeof rating === "undefined" || rating === null);

export const UPDATE_TOUR_LIKES_DISLIKES = gql`
  mutation update_listing_tour_likes_dislikes(
    $input: UpdateListingTourLikesDislikes!
  ) {
    update_listing_tour_likes_dislikes(input: $input) {
      likes
      dislikes
    }
  }
`;

function getOptions(keys = []) {
  return keys.map(key => TOUR_LIKES_OPTIONS[key]).filter(obj => obj);
}

const TourLikes = ({
  address,
  dislikes,
  likes,
  listingId,
  listingJourneyId,
  listingType,
  photoSrc,
  rating
}) => {
  const [modalLikesOpen, setModalLikesOpen] = useState(false);
  const [modalDisLikesOpen, setModalDisLikesOpen] = useState(false);

  const [tourLikes, setTourLikes] = useState(getOptions(likes));
  const [tourDisLikes, setTourDisLikes] = useState(getOptions(dislikes));

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isVisible, setVisible] = useState(showLikes(rating));

  const likesTitle = "What did you like?";
  const disLikesTitle = "What didn't you like?";

  const pushTourLikesToDataLayer = ({ selectedOptions, type }) => {
    if (window.dataLayer === undefined) return;

    const data = window.dataLayer.find(d => d.pageCat !== undefined) || {};
    const { pageCat = "unknown", pageType = "unknown" } = data;

    const eventLabel = `id=${listingJourneyId}|${type}=${selectedOptions.map(
      option => option.label
    )}`;

    window.dataLayer.push({
      event: "custom_event",
      event_category: `${pageCat}_${pageType}`,
      event_action: `submit_${type}`,
      event_label: eventLabel
    });
  };

  respondToTourRatingAdded({ listingId, listingType }, () => {
    setVisible(true);
  });

  const [updateTourLikesAndDislikes] = useMutation(UPDATE_TOUR_LIKES_DISLIKES);

  const handleSetSelectedItems = (selected, showConfirmationMessage = true) => {
    if (showConfirmationMessage) {
      setConfirmationOpen(true);
    }

    const [setSelected, type] = modalLikesOpen
      ? [setTourLikes, "likes"]
      : [setTourDisLikes, "dislikes"];

    setSelected(selected);
    updateTourLikesAndDislikes({
      variables: {
        input: {
          listing_id: listingId,
          listing_class: listingType.toLowerCase(),
          [type]: selected.map(option => option.key)
        }
      }
    });
  };

  const removeOption = (option, selectedItems, setSelected) => {
    const selected = [...selectedItems];
    const index = selected.indexOf(option);
    const type = setSelected === setTourLikes ? "likes" : "dislikes";

    if (index > -1) {
      selected.splice(index, 1);
    }

    updateTourLikesAndDislikes({
      variables: {
        input: {
          listing_id: listingId,
          listing_class: listingType.toLowerCase(),
          [type]: selected.map(opt => opt.key)
        }
      }
    });
    setSelected(selected);
  };

  const confirmationMessage = () => (
    <>
      Review saved. Access your notes on
      <span style={{ fontWeight: "bold" }}>&nbsp;{address}&nbsp;</span>
      in My Activity — anytime!
    </>
  );

  return (
    isVisible && (
      <>
        {confirmationOpen && (
          <Toast
            aria-label={`Review saved. Access your notes on ${address} in My Activity — anytime!`}
            message={confirmationMessage()}
            onHide={() => setConfirmationOpen(false)}
            theme="blue"
            time={4000}
          />
        )}
        <S.TourLikesWrapper>
          <S.TourLikesGroup>
            <p>{likesTitle}</p>

            <S.OptionsWrap>
              {tourLikes &&
                tourLikes
                  .sort((a, b) => a.position - b.position)
                  .map(option => (
                    <S.LikesPill key={option.key} value={option.key}>
                      <div>
                        <span>{option.label}</span>
                        <S.Close
                          ariaLabel={`Remove ${option.label} from Likes`}
                          onClick={() =>
                            removeOption(option, tourLikes, setTourLikes)
                          }
                          option={option.key}
                        />
                      </div>
                    </S.LikesPill>
                  ))}
              <S.Pill
                aria-label="Add Likes"
                onClick={() => setModalLikesOpen(true)}
              >
                <FontAwesomeIcon
                  color="primary"
                  fontSize="14px"
                  iconClass="plus"
                />
                <span> Add</span>
              </S.Pill>
            </S.OptionsWrap>
          </S.TourLikesGroup>

          <S.TourLikesGroup>
            <p>{disLikesTitle}</p>

            <S.OptionsWrap>
              {tourDisLikes &&
                tourDisLikes
                  .sort((a, b) => a.position - b.position)
                  .map(option => (
                    <S.DislikesPill key={option.key} value={option.key}>
                      <div>
                        <span>{option.label}</span>
                        <S.Close
                          ariaLabel={`Remove ${option.label} from Dislikes`}
                          onClick={() =>
                            removeOption(option, tourDisLikes, setTourDisLikes)
                          }
                          option={option.key}
                        />
                      </div>
                    </S.DislikesPill>
                  ))}
              <S.Pill
                aria-label="Add Dislikes"
                onClick={() => setModalDisLikesOpen(true)}
              >
                <FontAwesomeIcon
                  color="primary"
                  fontSize="14px"
                  iconClass="plus"
                />
                <span> Add</span>
              </S.Pill>
            </S.OptionsWrap>
          </S.TourLikesGroup>
        </S.TourLikesWrapper>
        {(modalLikesOpen || modalDisLikesOpen) &&
          ReactDOM.createPortal(
            <TourLikesModal
              address={address}
              isOpen={modalLikesOpen || modalDisLikesOpen}
              modalTitle={modalLikesOpen ? likesTitle : disLikesTitle}
              onClose={() =>
                modalLikesOpen
                  ? setModalLikesOpen(false)
                  : setModalDisLikesOpen(false)
              }
              photoSrc={photoSrc}
              pushTourLikesToDataLayer={pushTourLikesToDataLayer}
              selectedItems={modalLikesOpen ? tourLikes : tourDisLikes}
              setSelectedItems={handleSetSelectedItems}
              tourLikesOptions={TOUR_LIKES_OPTIONS}
              type={modalLikesOpen ? "likes" : "dislikes"}
            />,
            global.document.body
          )}
      </>
    )
  );
};

TourLikes.propTypes = {
  listingId: number.isRequired,
  listingJourneyId: number.isRequired,
  listingType: string.isRequired,
  photoSrc: string.isRequired,
  address: string.isRequired,
  rating: number,
  likes: arrayOf(string),
  dislikes: arrayOf(string)
};

TourLikes.defaultProps = {
  address: "",
  dislikes: [],
  likes: [],
  photoSrc: "",
  rating: null
};

export default TourLikes;
